import React, { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import { useNotification, useChurchConfig, useOptionList } from '../../hooks';
import {
  PageHeader, SubTabs, TablePanel, Notification, Drawer, Button, Badge, ConfirmDialog, PromptDialog, PrintButton
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { memberRegistrations } from '../../api/endpoints';
import type { MemberRegistration, RegistrationFormData } from '../../api/endpoints';
import { ApiError } from '../../api/client';
import RegistrationFormFields, { validateRegistration, withKeys, DEFAULT_OPTIONS } from './RegistrationFormFields';
import type { RegistrationOptions } from './RegistrationFormFields';
import { registrationPrintData, REGISTRATION_PRINT_MODULE, REGISTRATION_PRINT_DOCUMENT } from './registrationPrint';
import ApprovalProgress from '../approvals/ApprovalProgress';

// Families registered through the public link wait here for the office. Nothing
// becomes a member until the last approval level (Settings › Approvals) is
// approved, which creates the family, its head and every member in one step;
// after that the family can no longer edit it from the link. Any change — by
// the family or the office — sends it back to level 1.

type Status = 'PENDING' | 'REJECTED' | 'APPROVED';

const STATUS_TABS: Array<{ key: Status; label: string }> = [
  { key: 'PENDING', label: '⏳ Pending' },
  { key: 'REJECTED', label: '❌ Rejected' },
  { key: 'APPROVED', label: '✅ Approved' }
];

const formatWhen = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

interface Props {
  isDarkMode?: boolean;
  headerSwitch?: ReactNode;
  /** Called after an approve/reject so the tab's count badge can refresh. */
  onChanged?: () => void;
  /** Open this registration straight away (from the Approvals inbox). */
  openId?: number;
}

const PendingRegistrations = ({ isDarkMode, headerSwitch, onChanged, openId }: Props) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  const { notification, notify, dismiss } = useNotification();

  // The same parish settings the office's own member form follows.
  const churchConfig = useChurchConfig();
  const memberStatuses = useOptionList('church_member_statuses');
  const formOptions: RegistrationOptions = {
    memberStatuses: memberStatuses.options.length ? memberStatuses.options : DEFAULT_OPTIONS.memberStatuses,
    documentUploads: churchConfig.documentUploads !== false,
    guestMonths: Number(churchConfig.guestPeriodMonths) > 0 ? Number(churchConfig.guestPeriodMonths) : DEFAULT_OPTIONS.guestMonths
  };

  const [status, setStatus] = useState<Status>('PENDING');
  const [rows, setRows] = useState<MemberRegistration[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState<MemberRegistration | null>(null);
  const [draft, setDraft] = useState<RegistrationFormData | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [askReject, setAskReject] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, c] = await Promise.all([memberRegistrations.list(status), memberRegistrations.counts()]);
      setRows(list || []);
      setCounts(c || {});
    } catch (err) {
      notify((err as Error).message || 'Could not load registrations.', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (openId) openReview({ id: openId } as MemberRegistration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  /** Shows a registration whose form (with its documents) is already loaded. */
  const showReview = (reg: MemberRegistration) => {
    setOpen(reg);
    setDraft(reg.form ? withKeys(reg.form) : null);
    setDirty(false);
  };

  /** The list carries no forms — each one, documents included, is loaded on open. */
  const openReview = async (row: MemberRegistration) => {
    try {
      showReview(await memberRegistrations.get(row.id));
    } catch (err) {
      notify((err as Error).message || 'Could not open the registration.', 'error');
    }
  };

  const closeReview = () => { setOpen(null); setDraft(null); setDirty(false); };

  /** The family changed it meanwhile: show them the latest before anything is saved. */
  const reloadOpen = async (message: string) => {
    if (!open) return;
    notify(message, 'error');
    try {
      showReview(await memberRegistrations.get(open.id));
    } catch { /* the list reload below still runs */ }
    load();
  };

  const handleError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 409) { reloadOpen(err.message); return; }
    notify((err as Error).message || fallback, 'error');
  };

  /** Saves the office's edits; returns the saved registration, or null if it failed. */
  const save = async (): Promise<MemberRegistration | null> => {
    if (!open || !draft) return null;
    const problem = validateRegistration(draft);
    if (problem) { notify(problem, 'error'); return null; }
    try {
      const saved = await memberRegistrations.update(open.id, open.version, draft);
      showReview(saved);
      setDirty(false);
      return saved;
    } catch (err) {
      handleError(err, 'Could not save the changes.');
      return null;
    }
  };

  const handleSave = async () => {
    setBusy(true);
    const saved = await save();
    setBusy(false);
    if (saved) {
      notify('Changes saved. Approval starts again from level 1, so every approver sees the final version.', 'success');
      load();
      // The request's version changed: approval lists elsewhere must reload before anyone approves.
      window.dispatchEvent(new Event('approvals-changed'));
    }
  };

  const handleApprove = async () => {
    setConfirmApprove(false);
    if (!open) return;
    setBusy(true);
    try {
      // Approve exactly what is on screen: unsaved edits are saved first.
      const current = dirty ? await save() : open;
      if (!current) return;
      const done = await memberRegistrations.approve(current.id, current.version);
      if (done.status === 'APPROVED') {
        notify(`Approved. Family ${done.familyId} created with ${done.memberCount} member(s).`, 'success');
      } else {
        const a = done.approval;
        notify(`Level ${a?.approvedLevels} approved. Now waiting for level ${a?.nextLevel}: ${a?.waitingFor.join(' or ')}.`, 'success');
      }
      closeReview();
      load();
      onChanged?.();
      window.dispatchEvent(new Event('approvals-changed'));
    } catch (err) {
      handleError(err, 'Could not approve the registration.');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (reason: string) => {
    setAskReject(false);
    if (!open) return;
    setBusy(true);
    try {
      await memberRegistrations.reject(open.id, open.version, reason);
      notify('Registration rejected. The family will see the reason when they check the status.', 'success');
      closeReview();
      load();
      onChanged?.();
      window.dispatchEvent(new Event('approvals-changed'));
    } catch (err) {
      handleError(err, 'Could not reject the registration.');
    } finally {
      setBusy(false);
    }
  };

  const columns: Array<Column<MemberRegistration>> = [
    { key: 'referenceNo', header: 'Reference', nowrap: true,
      render: (r) => <span style={{ fontFamily: 'monospace', fontWeight: t.fontWeight.bold }}>{r.referenceNo}</span> },
    { key: 'headName', header: 'Family head',
      render: (r) => (
        <span>
          <strong style={{ color: t.textMain }}>{r.headName}</strong>
          {r.submittedBy && (
            <Badge tone="accent" isDarkMode={isDarkMode} style={{ marginLeft: '8px' }}>Added by {r.submittedBy}</Badge>
          )}
          {r.editedByFamily && r.status === 'PENDING' && (
            <Badge tone="warning" isDarkMode={isDarkMode} style={{ marginLeft: '8px' }}>Edited</Badge>
          )}
        </span>
      ) },
    { key: 'headMobile', header: 'Mobile', nowrap: true },
    { key: 'memberCount', header: 'People', align: 'center', width: '80px' },
    { key: 'submittedAt', header: 'Submitted', nowrap: true, render: (r) => formatWhen(r.submittedAt) },
    ...(status === 'PENDING'
      ? [{ key: 'approval', header: 'Approval',
          render: (r: MemberRegistration) => (r.approval && r.approval.status === 'PENDING' ? (
            <span>
              <Badge tone={r.approval.canAct ? 'accent' : 'warning'} isDarkMode={isDarkMode}>
                Level {r.approval.nextLevel} of {r.approval.levelsRequired}
              </Badge>
              <div style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '2px' }}>
                {r.approval.canAct ? 'Your turn' : `Waiting for ${r.approval.waitingFor.join(' or ')}`}
              </div>
            </span>
          ) : '—') } as Column<MemberRegistration>]
      : []),
    ...(status === 'APPROVED'
      ? [{ key: 'familyId', header: 'Family ID', nowrap: true,
          render: (r: MemberRegistration) => <strong>{r.familyId}</strong> } as Column<MemberRegistration>]
      : []),
    ...(status === 'REJECTED'
      ? [{ key: 'rejectReason', header: 'Reason',
          render: (r: MemberRegistration) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.rejectReason}</span> } as Column<MemberRegistration>]
      : []),
    ...(status !== 'PENDING'
      ? [{ key: 'reviewedBy', header: 'Reviewed', nowrap: true,
          render: (r: MemberRegistration) => `${r.reviewedBy || '—'} · ${formatWhen(r.reviewedAt)}` } as Column<MemberRegistration>]
      : []),
    { key: 'actions', header: '', align: 'right', width: '110px',
      render: (r) => (
        <Button size="sm" variant="secondary" isDarkMode={isDarkMode} onClick={(e) => { e.stopPropagation(); openReview(r); }}>
          {r.status === 'APPROVED' ? 'View' : 'Review'}
        </Button>
      ) }
  ];

  const editable = open && open.status !== 'APPROVED';
  const approval = open?.approval && open.approval.status === 'PENDING' ? open.approval : null;
  // Approving and rejecting follow the approval rules, not module access:
  // someone with Read Only access may still be an approver.
  const canAct = Boolean(open && open.status === 'PENDING' && (!approval || approval.canAct));
  const finalLevel = !approval || approval.nextLevel === approval.levelsRequired;

  // Prints the registration as shown (unsaved edits included) in the parish's
  // "Family Registration Form" layout from Settings › Print Templates.
  const printButton = open && draft && (
    <PrintButton
      module={REGISTRATION_PRINT_MODULE}
      documentType={REGISTRATION_PRINT_DOCUMENT}
      title={`Family-Registration-${open.referenceNo}`}
      data={() => registrationPrintData({ ...open, approvedAt: open.reviewedAt && open.status === 'APPROVED' ? open.reviewedAt : '' }, draft)}
      onNotify={notify}
      isDarkMode={isDarkMode}
    />
  );

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        leading={headerSwitch}
        icon={headerSwitch ? undefined : '📝'}
        title={headerSwitch ? undefined : 'Pending Approval'}
        isDarkMode={isDarkMode}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: t.spacing.md }}>
        <SubTabs
          value={status}
          onChange={(k) => setStatus(k as Status)}
          style={{ marginBottom: 0 }}
          isDarkMode={isDarkMode}
          tabs={STATUS_TABS.map((tab) => ({ ...tab, label: `${tab.label} (${counts[tab.key] ?? 0})` }))}
        />
        <Button variant="ghost" size="sm" isDarkMode={isDarkMode} onClick={load}>↻ Refresh</Button>
      </div>

      <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, margin: `0 0 ${t.spacing.md}` }}>
        Families who registered through the public link, and families added with Add Head while adding needs
        approval. Open one to check the details, correct anything,
        then <strong>Approve</strong> — the family and all its members are added to Members &amp; Families.
        The link is switched on and shared from Settings → Public Registration.
      </p>

      <TablePanel
        title={STATUS_TABS.find((x) => x.key === status)?.label.replace(/^\S+\s/, '') || 'Registrations'}
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={loading}
        onRowClick={openReview}
        emptyMessage={status === 'PENDING' ? 'No registrations are waiting for approval.' : 'Nothing here yet.'}
      />

      {open && draft && (
        <Drawer
          open
          width="880px"
          title={open.headName}
          subtitle={`${open.referenceNo} · ${open.memberCount} people · submitted ${formatWhen(open.submittedAt)}`}
          onClose={busy ? undefined : closeReview}
          isDarkMode={isDarkMode}
          footer={editable ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {printButton}
              {canAct && (
                <button type="button" disabled={busy} style={styles.dangerBtn} onClick={() => setAskReject(true)}>
                  ✕ Reject
                </button>
              )}
              {dirty && (
                <Button variant="secondary" write disabled={busy} isDarkMode={isDarkMode} onClick={handleSave}>
                  💾 Save changes
                </Button>
              )}
              {canAct ? (
                <button type="button" disabled={busy} style={styles.submitBtn} onClick={() => setConfirmApprove(true)}>
                  {finalLevel ? '✓ Approve & add to members' : `✓ Approve level ${approval?.nextLevel}`}
                </button>
              ) : (
                <Button variant="secondary" isDarkMode={isDarkMode} onClick={closeReview}>Close</Button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {printButton}
              <Button variant="secondary" isDarkMode={isDarkMode} onClick={closeReview}>Close</Button>
            </div>
          )}
        >
          {open.status === 'APPROVED' && (
            <p style={{ color: t.success, fontWeight: t.fontWeight.bold, marginTop: 0 }}>
              ✅ Approved by {open.reviewedBy} on {formatWhen(open.reviewedAt)} — Family {open.familyId},
              Membership No. {open.membershipNo}. Edit the family from the Heads / Members tabs.
            </p>
          )}
          {open.status === 'REJECTED' && (
            <p style={{ color: t.danger, marginTop: 0 }}>
              Rejected by {open.reviewedBy}: <em>{open.rejectReason}</em>. The family can correct and resubmit.
            </p>
          )}
          {open.approval && <ApprovalProgress approval={open.approval} t={t} />}
          {dirty && open.approval?.status === 'PENDING' && (open.approval.approvedLevels > 0) && (
            <p style={{ color: t.warning, marginTop: 0 }}>
              Saving your changes sends this back to level 1, so every approver sees the final version.
            </p>
          )}
          {open.editedByFamily && open.status === 'PENDING' && (
            <p style={{ color: t.warning, marginTop: 0 }}>
              ✏️ The family updated this on {formatWhen(open.updatedAt)}.
            </p>
          )}
          <RegistrationFormFields
            value={draft}
            onChange={(next) => { setDraft(next); setDirty(true); }}
            options={formOptions}
            onFileError={(msg) => notify(msg, 'error')}
            disabled={!editable || busy}
            isDarkMode={isDarkMode}
          />
        </Drawer>
      )}

      <ConfirmDialog
        open={confirmApprove}
        title={finalLevel ? 'Approve this family?' : `Approve level ${approval?.nextLevel}?`}
        message={
          finalLevel ? (
            <>
              This adds <strong>{open?.headName}</strong> as a new family with {draft ? 1 + draft.members.length : 0} member(s)
              to Members &amp; Families, with new Family and Member IDs. The family can no longer edit it from the link.
              {dirty && <><br /><br />Your unsaved changes will be saved first (and approval restarts at level 1).</>}
            </>
          ) : (
            <>
              You approve level {approval?.nextLevel} of {approval?.levelsRequired}. It then waits for level{' '}
              {(approval?.nextLevel || 0) + 1}: {approval?.levels[approval.nextLevel || 0]?.approvers.join(' or ')}.
              {dirty && <><br /><br />Your unsaved changes will be saved first (and approval restarts at level 1).</>}
            </>
          )
        }
        confirmLabel="Approve"
        onConfirm={handleApprove}
        onCancel={() => setConfirmApprove(false)}
        isDarkMode={isDarkMode}
      />

      <PromptDialog
        open={askReject}
        title="Reject registration"
        message="The family sees this reason when they check the status, and can correct and resubmit."
        placeholder="e.g. Please add the date of birth of all children"
        confirmLabel="Reject"
        required
        requiredError="Enter a reason for the family."
        multiline
        destructive
        onSubmit={handleReject}
        onCancel={() => setAskReject(false)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default PendingRegistrations;
